import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { VisualStyleData } from '@packages/global';

/**
 * VisualStyleStep 组件属性
 */
export interface VisualStyleStepProps {
  /** 步骤数据 */
  data: VisualStyleData | null;
  /** 是否正在编辑 */
  isEditing?: boolean;
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 编辑回调 */
  onEdit?: () => void;
  /** 取消编辑回调 */
  onCancelEdit?: () => void;
  /** 保存回调 */
  onSave?: () => void;
  /** 重新生成回调 */
  onRegenerate?: () => void;
  /** 数据变更回调 */
  onChange?: (data: Partial<VisualStyleData>) => void;
}

/**
 * 风格标签颜色配置
 */
const TAG_COLORS = [
  { bg: 'rgba(139, 92, 246, 0.2)', text: '#A78BFA' },   // 紫色
  { bg: 'rgba(59, 130, 246, 0.2)', text: '#93C5FD' },   // 蓝色
  { bg: 'rgba(236, 72, 153, 0.2)', text: '#F9A8D4' },   // 粉色
  { bg: 'rgba(34, 197, 94, 0.2)', text: '#86EFAC' },    // 绿色
  { bg: 'rgba(251, 146, 60, 0.2)', text: '#FDBA74' },   // 橙色
  { bg: 'rgba(56, 189, 248, 0.2)', text: '#7DD3FC' },   // 天蓝
];

/**
 * 从风格描述中提取标签
 */
function extractStyleTags(artStyle: string): string[] {
  // 简单的标签提取逻辑，可以根据实际需求调整
  const keywords = artStyle.match(/[\u4e00-\u9fa5]+|[a-zA-Z]+/g) || [];
  const uniqueKeywords = [...new Set(keywords)].filter(k => k.length >= 2);
  return uniqueKeywords.slice(0, 6);
}

/**
 * 空状态组件
 */
function EmptyState({ onRegenerate, t }: { onRegenerate?: () => void; t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-bg-elevated flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-gray-200 mb-2">{t('script:visualStyle.empty')}</h3>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">
        {t('script:visualStyle.emptyDesc')}
      </p>
      {onRegenerate && (
        <button
          onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" />
          {t('script:visualStyle.generate')}
        </button>
      )}
    </div>
  );
}

/**
 * 风格标签组件
 */
function StyleTag({ text, colorIndex }: { text: string; colorIndex: number }) {
  const color = TAG_COLORS[colorIndex % TAG_COLORS.length];
  return (
    <span
      className="px-3 py-1.5 rounded-md text-xs font-normal"
      style={{ backgroundColor: color.bg, color: color.text }}
    >
      {text}
    </span>
  );
}

/**
 * VisualStyleStep 组件
 * 
 * 显示视觉风格内容，包括：
 * - 风格名称
 * - 风格描述
 * - 风格标签
 * 
 * 设计参考：Pencil 设计 GxUnI 中的 CenterBody 节点
 * - gap: 20
 * - padding: 24
 * 
 * Requirements: 6.4
 */
export default function VisualStyleStep({
  data,
  isEditing = false,
  isLoading = false,
  onEdit,
  onCancelEdit,
  onSave,
  onRegenerate,
  onChange,
}: VisualStyleStepProps) {
  const { t } = useTranslation(['script', 'common']);
  const [localData, setLocalData] = useState<VisualStyleData | null>(data);

  // 同步外部数据变化（非编辑模式时）
  useEffect(() => {
    if (!isEditing && data) {
      setLocalData(data);
    }
  }, [data, isEditing]);

  // 处理字段变更
  const handleFieldChange = (field: keyof VisualStyleData, value: string) => {
    if (localData) {
      const updated = { ...localData, [field]: value };
      setLocalData(updated);
      onChange?.({ [field]: value });
    }
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-gray-400">{t('script:visualStyle.generating')}</span>
        </div>
      </div>
    );
  }

  // 空状态
  if (!data) {
    return <EmptyState onRegenerate={onRegenerate} t={t} />;
  }

  // 提取风格标签
  const styleTags = extractStyleTags(data.artStyle || '');

  return (
    <div className="space-y-5">
      {/* 风格名称区域 */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-[13px] font-medium text-gray-200">{t('script:visualStyle.name')}</label>
          {onEdit && !isEditing && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-50 transition-colors cursor-pointer"
            >
              <PencilIcon className="w-3.5 h-3.5" />
              {t('script:action.edit')}
            </button>
          )}
        </div>
        <div className="rounded-xl bg-bg-elevated p-4">
          {isEditing ? (
            <input
              type="text"
              value={localData?.styleName || ''}
              onChange={(e) => handleFieldChange('styleName', e.target.value)}
              className="w-full bg-transparent text-sm text-gray-50 outline-none"
              placeholder={t('script:visualStyle.namePlaceholder')}
            />
          ) : (
            <p className="text-sm text-gray-50">
              {data.styleName || t('script:visualStyle.unnamed')}
            </p>
          )}
        </div>
      </section>

      {/* 风格描述区域 */}
      <section className="space-y-3">
        <label className="text-[13px] font-medium text-gray-200">{t('script:visualStyle.description')}</label>
        <div className="rounded-xl bg-bg-elevated p-4">
          {isEditing ? (
            <textarea
              value={localData?.artStyle || ''}
              onChange={(e) => handleFieldChange('artStyle', e.target.value)}
              className="w-full bg-transparent text-sm text-gray-50 leading-relaxed outline-none resize-none min-h-[100px]"
              placeholder={t('script:visualStyle.descPlaceholder')}
            />
          ) : (
            <p className="text-sm text-gray-50 leading-relaxed">
              {data.artStyle || t('script:visualStyle.noDesc')}
            </p>
          )}
        </div>
      </section>

      {/* 基础提示词区域 */}
      <section className="space-y-3">
        <label className="text-[13px] font-medium text-gray-200">{t('script:visualStyle.basePrompt')}</label>
        <div className="rounded-xl bg-bg-elevated p-4">
          {isEditing ? (
            <textarea
              value={localData?.basePrompt || ''}
              onChange={(e) => handleFieldChange('basePrompt', e.target.value)}
              className="w-full bg-transparent text-sm text-gray-50 leading-relaxed outline-none resize-none min-h-[80px] font-mono"
              placeholder={t('script:visualStyle.promptPlaceholder')}
            />
          ) : (
            <p className="text-sm text-gray-50 leading-relaxed font-mono">
              {data.basePrompt || t('script:visualStyle.noPrompt')}
            </p>
          )}
        </div>
        {isEditing && (
          <p className="text-xs text-gray-400">
            {t('script:visualStyle.basePromptHint')}
          </p>
        )}
      </section>

      {/* 风格标签区域 */}
      {styleTags.length > 0 && (
        <section className="space-y-3">
          <label className="text-[13px] font-medium text-gray-200">{t('script:visualStyle.tags')}</label>
          <div className="flex flex-wrap gap-2">
            {styleTags.map((tag, index) => (
              <StyleTag key={index} text={tag} colorIndex={index} />
            ))}
          </div>
        </section>
      )}

      {/* 编辑模式下的保存/取消按钮 */}
      {isEditing && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
          <button
            onClick={onCancelEdit}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-50 transition-colors cursor-pointer"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
          >
            {t('script:action.save')}
          </button>
        </div>
      )}
    </div>
  );
}
